import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

/**
 * New home page UI chrome that must coordinate with global overlays (e.g. floating nav).
 * When the recommended product list category strip is position:sticky, mobile uses a full-width
 * glass `FloatingNavigation` bar (with an extra filter control wired via bump → RecommendedSection).
 */
export interface NewHomeUiState {
  /** Mobile: recommended section category bar has scrolled into its stuck (pinned) state */
  mobileRecommendedFilterStripStuck: boolean;
  /** V7 mobile: top of the Recommended section has reached the header handoff line */
  mobileRecommendedSectionReached: boolean;
  /** Desktop (md+): new home recommended browse category strip is `position:sticky` and pinned — navbar defers stickiness */
  desktopRecommendedBrowseFilterStripStuck: boolean;
  /** Recommended browse has facet rows from API — used to enable the stuck-mode filter control */
  recommendedBrowseFiltersEnabled: boolean;
  /**
   * Incremented from FloatingNavigation filter button; RecommendedSection syncs to open/close
   * the same sheet as the in-flow filter chip (toggle).
   */
  recommendedBrowseFilterButtonNonce: number;
}

const initialState: NewHomeUiState = {
  mobileRecommendedFilterStripStuck: false,
  mobileRecommendedSectionReached: false,
  desktopRecommendedBrowseFilterStripStuck: false,
  recommendedBrowseFiltersEnabled: false,
  recommendedBrowseFilterButtonNonce: 0,
};

const newHomeUiSlice = createSlice({
  name: 'newHomeUi',
  initialState,
  reducers: {
    setMobileRecommendedFilterStripStuck(state, action: PayloadAction<boolean>) {
      state.mobileRecommendedFilterStripStuck = action.payload;
    },
    setMobileRecommendedSectionReached(state, action: PayloadAction<boolean>) {
      state.mobileRecommendedSectionReached = action.payload;
    },
    setDesktopRecommendedBrowseFilterStripStuck(state, action: PayloadAction<boolean>) {
      state.desktopRecommendedBrowseFilterStripStuck = action.payload;
    },
    setRecommendedBrowseFiltersEnabled(state, action: PayloadAction<boolean>) {
      state.recommendedBrowseFiltersEnabled = action.payload;
    },
    bumpRecommendedBrowseFilterButton(state) {
      state.recommendedBrowseFilterButtonNonce += 1;
    },
  },
});

export const {
  setMobileRecommendedFilterStripStuck,
  setMobileRecommendedSectionReached,
  setDesktopRecommendedBrowseFilterStripStuck,
  setRecommendedBrowseFiltersEnabled,
  bumpRecommendedBrowseFilterButton,
} = newHomeUiSlice.actions;
export const newHomeUiReducer = newHomeUiSlice.reducer;

export function selectMobileRecommendedFilterStripStuck(
  state: { newHomeUi?: NewHomeUiState }
): boolean {
  return state.newHomeUi?.mobileRecommendedFilterStripStuck ?? false;
}

export function selectMobileRecommendedSectionReached(
  state: { newHomeUi?: NewHomeUiState }
): boolean {
  return state.newHomeUi?.mobileRecommendedSectionReached ?? false;
}

export function selectDesktopRecommendedBrowseFilterStripStuck(
  state: { newHomeUi?: NewHomeUiState }
): boolean {
  return state.newHomeUi?.desktopRecommendedBrowseFilterStripStuck ?? false;
}

export function selectRecommendedBrowseFiltersEnabled(
  state: { newHomeUi?: NewHomeUiState }
): boolean {
  return state.newHomeUi?.recommendedBrowseFiltersEnabled ?? false;
}
